import { OpenType, SourceType } from "@/constants";
import request, { MethodEnum, serverTriggerUrl } from "@/utils/fetch";

/** 顶层频道 slug（前后端约定常量值写死） */
export enum TopLevelChannels {
  /** 首页推荐频道 slug */
  home_page_recommended_channel = "home-page-recommended-channel",
  /** 首页广场频道 slug */
  home_page_plaza_channel = "home-page-plaza-channel",
  /** 首页活动频道 slug */
  home_page_activity_channel = "home-page-activity-channel",
  /** 首页咨询频道 slug */
  home_page_consult_channel = "home-page-consult-channel",
  /** 设备圈 */
  equipment_circle_channel = "equipment-circle-channel",
  /** 搜索结果页 */
  search_result_channel = "search-result-channel",
}

/** 频道筛选类型 */
export enum ChannelSortType {
  /** 最新发布 */
  latest_posts = "latest_posts",
  /** 最多评论 */
  most_comments = "most_comments",
  /** 最多浏览 */
  most_views = "most_views",
  /** 最新评论 */
  latest_comments = "latest_comments",
  /** 最热 */
  hottest = "hottest",
  /** 导入顺序 */
  import_order = 'import_order',
  /** 默认 */
  default = 'default',
  /** 发布时间 */
  post_time = 'post_time',
  /** 最多浏览 */
  views = 'views',
}

/** 子频道树 Request 类型 */
export interface ISubChannelTreeReq {
  /** 频道 channel 的 slug  */
  slug: string;
  /** 向下层级数（默认为 1） */
  level_num?: number;
}

export interface IChannelDetailReq {
  slug: string;
}

interface VideoDefinition {
  src: string;
  type: string;
  text: string;
}

interface VideoPlayInfo {
  video_uid: string;
  video_cover: string;
  video_duration: string;
  definitions: VideoDefinition[];
}

export type BannerConfigType = {
  title: string;
  weight: number;
  tags: string[];
  link_url: string;
  openType: OpenType;
  open_type: OpenType;
  m_banner_url: string;
  use_terminal: string;
  pc_banner_url: string;
  video_play_info: VideoPlayInfo;
};

export type JumpChannelInfo = {
  slug: string;
  name: string;
  avatar_image: string;
  /** 设备主题的运营标识，如：热卖机型 */
  operation_label?: string;
};

interface SimpleThread {
  tid: number;
  subject: string;
}

interface SimpleChannelList {
  slug: string;
  name: string;
  weight: number | React.ReactNode;
  hot_text: string;
  avatar_image: string;
  display_type: string | null;
}
export interface CommonComponent {
  title: string;
  weight: number;
  type: SourceType;
  tid_list: number[];
  simple_thread_list?: SimpleThread[];
  simple_channel_list?: SimpleChannelList[];
  external_info_list?: ExternalInfo[];
}

interface ExternalInfo {
  link_url: string;
  pc_url: string;
  weight: string;
  open_type: number;
  is_valid: boolean;
  murl: string | null;
  title?: string;
}

export interface PreRollsComponent {
  title: string;
  type: SourceType;
  weight: number;
  external_info_list: ExternalInfo[];
}

export interface IChannelConfig {
  /** 头部 banner 列表 */
  banner_config: BannerConfigType[];
  /** 可跳转的频道 slugs */
  jump_channel_slugs: string[];
  /** 可跳转的频道信息列表 */
  jump_channel_infos: JumpChannelInfo[];
  /** 热门话题列表 */
  hot_topic_config: IhotTopicConfigItem[];
  /** 运营标识 */
  operation_label: string;
  /** 背景图  */
  background_image: string;
  /** PC 背景图 */
  pc_background_image: string;
  /** 贴片广告/快捷入口 */
  common_component_list: CommonComponent[];
}

export interface IhotTopicConfigItem {
  title: string;
  link: string;
  weight: string;
  /** 链接打开方式 */
  open_type: OpenType;
}

/** 子频道数 Response 类型 */
export interface ISubChannelTreeRes {
  /** 其他配置 */
  channel_config: IChannelConfig;
  /** 频道 ID */
  id: number;
  /** 频道名称 */
  name: string;
  /** 备注 */
  note: string;
  /** 支持的排序规则 */
  order_rule: ChannelSortType[];
  /** 父频道 ID */
  parent_id: number;
  /** 频道 Slug */
  slug: string;
  /** 频道权重 */
  weight: number;
  /** 子频道 */
  sub_channel_info_dtos: ISubChannelTreeRes[];
  /** 设备图标 */
  avatar_image: string;
}

/** 单一圈子下的列表 */
type SubCircleList = {
  slug: string;
  name: string;
  weight: number;
  avatar_image?: string;
};

/** 单一圈子类型 */
export type ChannelCircle = {
  slug: string;
  name: string;
  weight: number;
  avatar_image?: string;
  /** 单一圈子下的列表 */
  sub_circle_list: SubCircleList[];
};

/** 全部圈子返回结果 */
export interface IAllChannelCirclesRes {
  items: ChannelCircle[];
}

/** 热帖类型 */
export interface IHotThread {
  hot_score: number;
  tid: number;
  title: string;
}

export interface IHotThreadsRes {
  items: IHotThread[];
}

/**
 * 获取子频道树
 * @param slug 子频道 Slug
 * @param level_num 向下层级数
 * @param isServerTrigger 是否是服务端请求
 * @returns Promise<ISubChannelTreeRes>
 */
export const getSubChannelTree = (
  {
    slug = TopLevelChannels.home_page_recommended_channel,
    level_num,
  }: ISubChannelTreeReq,
  isServerTrigger = false,
  headers = {}
): Promise<ISubChannelTreeRes> => {
  const prefix = isServerTrigger ? serverTriggerUrl : "";
  const key = "GetSubChannelTree";
  return request({
    isServerTrigger,
    key,
    headers,
    url: `${prefix}/forum/channel/sub-channel-tree`,
    method: MethodEnum.GET,
    params: {
      slug,
      level_num,
    },
  }).then((data) => data?.item || {});
};

/**
 * 获取频道详情
 * @param slug 频道 Slug
 * @param isServerTrigger 是否是服务端请求
 * @returns Promise<ISubChannelTreeRes>
 */
export const getChannelDetail = (
  { slug = TopLevelChannels.home_page_recommended_channel }: IChannelDetailReq,
  isServerTrigger = false,
  headers = {}
): Promise<ISubChannelTreeRes> => {
  const prefix = isServerTrigger ? serverTriggerUrl : "";
  const key = "GetChannelDetail";
  return request({
    isServerTrigger,
    key,
    headers,
    url: `${prefix}/forum/channel/detail/${slug}`,
    method: MethodEnum.GET,
  }).then((data) => data?.item || {});
};

/**
 * 获取全部圈子
 * @param isServerTrigger 是否是服务端请求
 * @returns Promise<IAllChannelCirclesRes>
 */
export const getAllChannelCircles = (
  isServerTrigger = false,
  headers = {}
): Promise<IAllChannelCirclesRes> => {
  const prefix = isServerTrigger ? serverTriggerUrl : "";
  const key = "GetAllChannelCircles";
  return request({
    isServerTrigger,
    key,
    headers,
    url: `${prefix}/forum/channel/all-circle`,
    method: MethodEnum.GET,
  }).then((data) => data?.items || []);
};

/**
 * 获取广场页社区热点
 */
export const getHotThreads = (
  isServerTrigger = false,
  headers = {}
): Promise<IHotThread[]> => {
  const prefix = isServerTrigger ? serverTriggerUrl : "";
  const key = "GetHotThreads";
  return request({
    isServerTrigger,
    key,
    headers,
    url: `${prefix}/forum/billboard/hot-threads`,
    method: MethodEnum.GET,
  }).then((data) => data?.items || []);
};
